"use client";

import {
  Button,
  FormNumber,
  FormRoot,
  FormTextarea,
  Input,
  Label,
  Loader,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeftIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { Resolver } from "react-hook-form";
import { useFieldArray } from "react-hook-form";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  type ShopSellableCatalogRow,
  shopCatalogApi,
} from "@/modules/merchant/presentation/api/client";
import {
  usePatchInventoryLotScanCodeMutation,
  usePurchaseOrderDetailQuery,
  useReceivePurchaseOrderGoodsMutation,
  useShopInventoryBranchSkuStock,
  useShopInventoryLotLabelsQuery,
} from "@/modules/merchant/presentation/api/queries";
import {
  buildDefaultUnitScanCode,
  printLotLabelCode128,
  ReceiveFormSchema,
  type ReceiveFormValues,
  SerialLotGroupBlock,
  trackingLabel,
} from "@/modules/merchant/presentation/inventory/receive-goods-shared";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import {
  InventorySkuStockBanner,
  InventoryStockHero,
  InventoryStockLoading,
} from "@/modules/merchant/presentation/ui/InventorySkuStockBanner";
import { PurchaseOrderStatusBadge } from "@/modules/merchant/presentation/ui/PurchaseOrderStatusBadge";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { ShopWarehouseLocationPickerRemote } from "@/modules/merchant/presentation/ui/ShopWarehouseLocationPicker";
import { hasShopFeature } from "@/modules/merchant/presentation/lib/has-shop-feature";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { formatShopMoney } from "@/shared/lib/currency";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";

const PO_RECEIVE_ROUTE =
  "/app/shops/$shopId/purchase-orders/$purchaseOrderId/receive" as const;
const PO_DETAIL_ROUTE =
  "/app/shops/$shopId/purchase-orders/$purchaseOrderId" as const;

type PoReceiveSearch = {
  shopProductSkuId?: string;
};

function defaultReceiveFormValues(branchId: string): ReceiveFormValues {
  return {
    branchId,
    locationId: "",
    qty: 1,
    lots: [{ lotNo: "", qty: 1, manufacturedDate: "", expiryDate: "" }],
    serialLotGroups: [
      { lotNo: "", items: [{ serialNo: "", unitScanCode: "" }] },
    ],
    note: "",
  };
}

function cloneReceiveFormValues(v: ReceiveFormValues): ReceiveFormValues {
  return {
    branchId: v.branchId ?? "",
    locationId: v.locationId ?? "",
    qty: v.qty,
    lots: v.lots.map((l) => ({ ...l })),
    serialLotGroups: v.serialLotGroups.map((g) => ({
      lotNo: g.lotNo,
      items: g.items.map((it) => ({ ...it })),
    })),
    note: v.note ?? "",
  };
}

function receiptModeFromTracking(raw: string): "none" | "lot" | "lot_serial" {
  if (raw === "lot" || raw === "lot_serial") return raw;
  if (raw === "serial") return "lot_serial";
  return "none";
}

type PoReceiptLinePayload = {
  shopProductSkuId: string;
  locationId?: string;
  qty?: number;
  lots?: Array<{
    lotNo: string;
    qty: number;
    manufacturedDate?: string;
    expiryDate?: string;
  }>;
  serialReceipts?: Array<{
    serialNo: string;
    lotNo: string;
    unitScanCode?: string;
  }>;
};

type BuildPoDraftResult =
  | { kind: "skip" }
  | { kind: "error"; message: string }
  | { kind: "line"; line: PoReceiptLinePayload };

function buildPoReceiptLineFromDraft(
  skuId: string,
  draft: ReceiveFormValues,
  remaining: number,
  mode: "none" | "lot" | "lot_serial",
): BuildPoDraftResult {
  if (mode === "none") {
    const qty = draft.qty;
    if (qty < 1) return { kind: "skip" };
    if (qty > remaining) {
      return {
        kind: "error",
        message: `ຮັບໄດ້ບໍ່ເກີນ ${remaining} ຕາມ PO`,
      };
    }
    return {
      kind: "line",
      line: { shopProductSkuId: skuId, qty },
    };
  }

  if (mode === "lot") {
    const lots = draft.lots
      .map((l) => {
        const m = l.manufacturedDate.trim();
        const e = l.expiryDate.trim();
        return {
          lotNo: l.lotNo.trim(),
          qty: l.qty,
          ...(m ? { manufacturedDate: m } : {}),
          ...(e ? { expiryDate: e } : {}),
        };
      })
      .filter((l) => l.lotNo.length > 0);
    if (lots.length === 0) return { kind: "skip" };
    const sum = lots.reduce((s, l) => s + l.qty, 0);
    if (sum > remaining) {
      return {
        kind: "error",
        message: `ຮັບລວມບໍ່ເກີນ ${remaining} ຕາມ PO`,
      };
    }
    return {
      kind: "line",
      line: { shopProductSkuId: skuId, lots },
    };
  }

  const serialReceipts: {
    serialNo: string;
    lotNo: string;
    unitScanCode?: string;
  }[] = [];
  for (const g of draft.serialLotGroups) {
    const lotNo = g.lotNo.trim();
    for (const it of g.items) {
      const serialNo = it.serialNo.trim();
      if (!serialNo) continue;
      if (!lotNo) {
        return {
          kind: "error",
          message: "ໃສ່ Lot no. ກ່ອນ ສຳລັບທຸກກຸ່ມທີ່ມີ serial",
        };
      }
      let unitScan = it.unitScanCode?.trim() ?? "";
      if (!unitScan) {
        unitScan = buildDefaultUnitScanCode(lotNo, serialNo);
      }
      serialReceipts.push({
        serialNo,
        lotNo,
        ...(unitScan ? { unitScanCode: unitScan } : {}),
      });
    }
  }
  if (serialReceipts.length === 0) return { kind: "skip" };
  if (serialReceipts.length > remaining) {
    return {
      kind: "error",
      message: `ຮັບໄດ້ບໍ່ເກີນ ${remaining} ຊິ້ນຕາມ PO`,
    };
  }
  return {
    kind: "line",
    line: { shopProductSkuId: skuId, serialReceipts },
  };
}

export function ShopPurchaseOrderReceivePage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const purchaseOrderId = params?.purchaseOrderId as string | undefined;
  const search = (useSearch({ strict: false }) ?? {}) as PoReceiveSearch;
  const nav = useNavigate();

  const poDetail = usePurchaseOrderDetailQuery(shopId, purchaseOrderId);
  const d = poDetail.data;

  const receivableLines = useMemo(
    () => d?.lines.filter((l) => l.qtyOrdered > l.qtyReceived) ?? [],
    [d?.lines],
  );

  const urlSku = search.shopProductSkuId?.trim();
  const selectedSkuId = useMemo(() => {
    if (!receivableLines.length) return undefined;
    if (urlSku && receivableLines.some((l) => l.shopProductSkuId === urlSku))
      return urlSku;
    return receivableLines[0]?.shopProductSkuId;
  }, [receivableLines, urlSku]);

  const setSelectedSku = (id: string) => {
    if (!shopId || !purchaseOrderId) return;
    nav({
      to: PO_RECEIVE_ROUTE,
      params: { shopId, purchaseOrderId },
      search: { shopProductSkuId: id },
      replace: true,
    });
  };

  const skuQuery = useQuery({
    queryKey: ["shops", shopId, "po-receive", "sku", selectedSkuId],
    queryFn: () => {
      if (!shopId || !selectedSkuId) throw new Error("missing");
      return shopCatalogApi.getSellableSku(shopId, selectedSkuId);
    },
    enabled: !!shopId && !!selectedSkuId,
  });

  const activeSku: ShopSellableCatalogRow | null = skuQuery.data ?? null;

  const receivePo = useReceivePurchaseOrderGoodsMutation(shopId);
  const patchLotScan = usePatchInventoryLotScanCodeMutation(shopId);
  const { currentShop } = useMerchantShops(shopId);
  const warehouseLocationsOn = hasShopFeature(
    currentShop?.featureFlags,
    "warehouse_locations",
  );
  const [defaultReceiveLocationId, setDefaultReceiveLocationId] = useState("");
  const [lotScanDraftById, setLotScanDraftById] = useState<
    Record<string, string>
  >({});

  /** ຮີດຟອມຕໍ່ບັນທັດ SKU — ບໍ່ໃຫ້ຫາຍເມື່ອສະລັບແຖວໃນຕາຕາ PO */
  const receiveDraftsBySkuRef = useRef<Record<string, ReceiveFormValues>>({});
  const prevSelectedSkuRef = useRef<string | undefined>(undefined);

  const form = RHF.useForm<ReceiveFormValues>({
    resolver: zodResolver(ReceiveFormSchema) as Resolver<ReceiveFormValues>,
    defaultValues: {
      branchId: "",
      locationId: "",
      qty: 1,
      lots: [{ lotNo: "", qty: 1, manufacturedDate: "", expiryDate: "" }],
      serialLotGroups: [
        { lotNo: "", items: [{ serialNo: "", unitScanCode: "" }] },
      ],
      note: "",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "lots",
  });

  const {
    fields: serialGroupFields,
    append: appendSerialGroup,
    remove: removeSerialGroup,
  } = useFieldArray({
    control: form.control,
    name: "serialLotGroups",
  });

  useEffect(() => {
    if (!d) return;
    form.setValue("branchId", d.branchId, { shouldValidate: true });
  }, [d, form]);

  useEffect(() => {
    if (!d || !selectedSkuId) return;

    const prev = prevSelectedSkuRef.current;
    if (prev !== undefined && prev !== selectedSkuId) {
      receiveDraftsBySkuRef.current[prev] = cloneReceiveFormValues(
        form.getValues(),
      );
    }
    prevSelectedSkuRef.current = selectedSkuId;

    const saved = receiveDraftsBySkuRef.current[selectedSkuId];
    const branchId = d.branchId;
    if (saved) {
      form.reset({
        ...saved,
        branchId,
      });
    } else {
      form.reset(defaultReceiveFormValues(branchId));
    }
  }, [d, selectedSkuId, form]);

  useEffect(() => {
    setLotScanDraftById({});
  }, [selectedSkuId]);

  const rawTracking = activeSku?.trackingMode;
  const mode =
    rawTracking === "lot" || rawTracking === "lot_serial"
      ? rawTracking
      : rawTracking === "serial"
        ? "lot_serial"
        : "none";

  const branchIdForStock = d?.branchId?.trim() || undefined;

  const branchStock = useShopInventoryBranchSkuStock(shopId, {
    branchId: branchIdForStock,
    shopProductSkuId: selectedSkuId,
  });
  const stockLine = branchStock.data?.data[0];
  const lotLabelsQuery = useShopInventoryLotLabelsQuery(shopId, {
    branchId: branchIdForStock,
    shopProductSkuId: selectedSkuId,
    enabled: mode === "lot",
  });

  const selectedLine = useMemo(
    () => d?.lines.find((l) => l.shopProductSkuId === selectedSkuId),
    [d?.lines, selectedSkuId],
  );

  const poRemaining = selectedLine
    ? selectedLine.qtyOrdered - selectedLine.qtyReceived
    : 0;

  const serialGroupsWatched = form.watch("serialLotGroups");
  const totalSerialRows = useMemo(
    () =>
      serialGroupsWatched.reduce((sum, g) => sum + (g.items?.length ?? 0), 0),
    [serialGroupsWatched],
  );
  /** ຈຳນວນແຖວ serial ລວມທຸກກຸ່ມ — ບໍ່ເກີນຈຳນວນຄ້າງຮັບຕາມ PO */
  const serialRowsAtPoLimit = totalSerialRows >= poRemaining;

  const lotsWatched = form.watch("lots");
  const lotQtySum = useMemo(
    () =>
      lotsWatched.reduce((s, l) => {
        const q = Number(l.qty);
        return s + (Number.isFinite(q) ? q : 0);
      }, 0),
    [lotsWatched],
  );
  /** ລວມຈຳນວນ lot ຮອດຂີດຄ້າງຮັບ PO — ປິດເພີ່ມແຖວ lot */
  const lotQtyAtPoLimit = poRemaining > 0 && lotQtySum >= poRemaining;

  const onSubmit = async (vals: ReceiveFormValues) => {
    if (!shopId || !purchaseOrderId || !d) return;
    const branchId = vals.branchId?.trim();
    if (!branchId) {
      toast.error("ເລືອກສາຂາ");
      return;
    }
    if (!selectedSkuId) return;

    receiveDraftsBySkuRef.current[selectedSkuId] = cloneReceiveFormValues(
      form.getValues(),
    );

    const merged: Record<string, ReceiveFormValues> = {
      ...receiveDraftsBySkuRef.current,
    };

    const receiptLines: PoReceiptLinePayload[] = [];
    const noteParts: string[] = [];

    const orderedSkuIds = d.lines
      .filter((l) => l.qtyOrdered > l.qtyReceived)
      .map((l) => l.shopProductSkuId)
      .filter((id) => merged[id] != null);

    for (const skuId of orderedSkuIds) {
      const draft = merged[skuId];
      if (!draft) continue;
      const poLine = d.lines.find((l) => l.shopProductSkuId === skuId);
      if (!poLine) continue;
      const remaining = poLine.qtyOrdered - poLine.qtyReceived;
      if (remaining <= 0) continue;

      const lineMode = receiptModeFromTracking(poLine.trackingMode);
      const built = buildPoReceiptLineFromDraft(
        skuId,
        draft,
        remaining,
        lineMode,
      );

      if (built.kind === "error") {
        toast.error(
          `${poLine.productName} (${poLine.shopSku}): ${built.message}`,
        );
        return;
      }
      if (built.kind === "skip") continue;

      const lineLocationId = draft.locationId?.trim();
      receiptLines.push({
        ...built.line,
        ...(lineLocationId ? { locationId: lineLocationId } : {}),
      });
      const n = draft.note?.trim();
      if (n) noteParts.push(n);
    }

    if (receiptLines.length === 0) {
      toast.error(
        "ບໍ່ມີບັນທັດທີ່ກອກຂໍ້ມູນຮັບ — ກອກຢ່າງນ້ອຍ 1 ບັນທັດໃນຕາຕາ PO (ຫຼື ກວດວ່າມີ lot/serial ຄົບ)",
      );
      return;
    }

    let mergedNote = noteParts.join("\n").trim() || undefined;
    if (mergedNote && mergedNote.length > 2000) {
      mergedNote = mergedNote.slice(0, 2000);
    }

    const idempotencyKey =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `po-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    try {
      await receivePo.mutateAsync({
        purchaseOrderId,
        body: {
          lines: receiptLines,
          note: mergedNote,
          defaultLocationId: defaultReceiveLocationId.trim() || undefined,
        },
        idempotencyKey,
      });
      receiveDraftsBySkuRef.current = {};
      prevSelectedSkuRef.current = undefined;
      nav({
        to: PO_DETAIL_ROUTE,
        params: { shopId, purchaseOrderId },
      });
    } catch {
      // toast ຈາກ mutation
    }
  };

  const saveLotScanCode = async (lotId: string) => {
    const draft = lotScanDraftById[lotId];
    await patchLotScan.mutateAsync({
      lotId,
      body: { lotScanCode: draft == null ? null : draft },
    });
  };

  const regenerateLotScanCode = async (lotId: string) => {
    await patchLotScan.mutateAsync({
      lotId,
      body: { regenerate: true },
    });
  };

  if (!shopId || !purchaseOrderId) {
    return null;
  }

  if (poDetail.isLoading) {
    return (
      <RequireMerchantPermission
        permission={MerchantPermissions.inventory.receive}
      >
        <Header>
          <Breadcrumb items={shopBreadcrumbs.purchaseOrderReceive(shopId)} />
        </Header>
        <Main>
          <div className="flex justify-center py-16">
            <Loader />
          </div>
        </Main>
      </RequireMerchantPermission>
    );
  }

  if (!d) {
    return (
      <RequireMerchantPermission
        permission={MerchantPermissions.inventory.receive}
      >
        <Header>
          <Breadcrumb items={shopBreadcrumbs.purchaseOrderReceive(shopId)} />
        </Header>
        <Main>
          <p className="text-muted-foreground">ບໍ່ພົບໃບສັ່ງຊື້</p>
        </Main>
      </RequireMerchantPermission>
    );
  }

  const canReceiveFlow =
    d.status === "submitted" || d.status === "partially_received";

  if (!canReceiveFlow) {
    return (
      <RequireMerchantPermission
        permission={MerchantPermissions.inventory.receive}
      >
        <Header>
          <Breadcrumb
            items={shopBreadcrumbs.purchaseOrderReceive(shopId, d.poNumber)}
          />
        </Header>
        <Main>
          <div className="mx-auto max-w-lg space-y-4 rounded-xl border border-border bg-card p-6 text-center">
            <p className="text-muted-foreground text-sm">
              ໃບສັ່ງຊື້ນີ້ບໍ່ພ້ອມຮັບສິນຄ້າ (ສະຖານະ: {d.status})
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                nav({
                  to: PO_DETAIL_ROUTE,
                  params: { shopId, purchaseOrderId },
                })
              }
            >
              ກັບລາຍລະອຽດ PO
            </Button>
          </div>
        </Main>
      </RequireMerchantPermission>
    );
  }

  if (receivableLines.length === 0) {
    return (
      <RequireMerchantPermission
        permission={MerchantPermissions.inventory.receive}
      >
        <Header>
          <Breadcrumb
            items={shopBreadcrumbs.purchaseOrderReceive(shopId, d.poNumber)}
          />
        </Header>
        <Main>
          <div className="mx-auto max-w-lg space-y-4 rounded-xl border border-border bg-card p-6 text-center">
            <p className="font-medium">ຮັບຄົບທຸກບັນທັດແລ້ວ</p>
            <Button
              type="button"
              onClick={() =>
                nav({
                  to: PO_DETAIL_ROUTE,
                  params: { shopId, purchaseOrderId },
                })
              }
            >
              ກັບລາຍລະອຽດ PO
            </Button>
          </div>
        </Main>
      </RequireMerchantPermission>
    );
  }

  return (
    <RequireMerchantPermission
      permission={MerchantPermissions.inventory.receive}
    >
      <Header>
        <Breadcrumb
          items={shopBreadcrumbs.purchaseOrderReceive(shopId, d.poNumber)}
        />
      </Header>

      <Main>
        <div className="mx-auto max-w-4xl space-y-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-muted-foreground text-xs">ຮັບຂອງຕາມໃບສັ່ງຊື້</p>
              <h1 className="font-heading font-semibold text-xl tracking-tight">
                {d.poNumber}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <PurchaseOrderStatusBadge status={d.status} />
                <span className="text-muted-foreground text-sm">
                  ສະກຸນ{" "}
                  <span className="font-mono text-card-foreground">
                    {d.currency}
                  </span>
                </span>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-1 self-start"
              onClick={() =>
                nav({
                  to: PO_DETAIL_ROUTE,
                  params: { shopId, purchaseOrderId },
                })
              }
            >
              <ArrowLeftIcon className="size-4" aria-hidden />
              ກັບລາຍລະອຽດ
            </Button>
          </div>

          <div className="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-5">
            <div className="grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <p className="text-muted-foreground text-xs">ສາຂາຮັບເຂົ້າ</p>
                <p className="font-medium">
                  {d.branchName?.trim() || d.branchId.slice(0, 8)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">ຜູ້ສະໜອງ</p>
                <p className="font-medium">{d.supplierName?.trim() || "—"}</p>
              </div>
            </div>
          </div>

          {warehouseLocationsOn && branchIdForStock ? (
            <div className="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-5">
              <p className="font-medium text-sm">ຊ່ອງຮັບເຂົ້າ default (ທຸກບັນທັດ)</p>
              <p className="mt-1 text-muted-foreground text-xs">
                ວ່າງໄວ້ = ໃຊ້ຊ່ອງ default ຂອງສາຂາ (MAIN-DEFAULT) — ບັນທັດທີ່ກຳນົດຊ່ອງເອງຈະ override
              </p>
              <ShopWarehouseLocationPickerRemote
                shopId={shopId}
                branchId={branchIdForStock}
                value={defaultReceiveLocationId}
                onValueChange={setDefaultReceiveLocationId}
                placeholder="default receive (ວ່າງ = MAIN-DEFAULT)"
                className="mt-3 w-full max-w-md"
              />
            </div>
          ) : null}

          <div className="rounded-xl border border-border bg-card shadow-sm">
            <div className="border-border border-b bg-muted/30 px-4 py-3">
              <h2 className="font-medium text-sm">
                ລາຍການຕາມ PO — ເລືອກບັນທັດທີ່ກຳລັງຮັບ
              </h2>
              <p className="mt-1 text-muted-foreground text-xs">
                ແຕະແຖວແລ້ວກອກຈຳນວນ/lot/serial ຂ້າງລຸ່ມ — ບໍ່ເກີນຈຳນວນຄ້າງຮັບ
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead className="border-border border-b bg-muted/40">
                  <tr>
                    <th className="px-3 py-2">ສິນຄ້າ</th>
                    <th className="px-3 py-2">SKU</th>
                    <th className="px-3 py-2">ຕິດຕາມ</th>
                    <th className="px-3 py-2 text-right">ສັ່ງ / ຮັບແລ້ວ</th>
                    <th className="px-3 py-2 text-right">ຄ້າງຮັບ</th>
                    <th className="px-3 py-2 text-right">ຕົ້ນທຶນ</th>
                  </tr>
                </thead>
                <tbody>
                  {d.lines.map((line) => {
                    const remaining = line.qtyOrdered - line.qtyReceived;
                    const receivable = remaining > 0;
                    const selected =
                      line.shopProductSkuId === selectedSkuId && receivable;
                    return (
                      <tr
                        key={line.id}
                        className={
                          selected
                            ? "border border-secondary/40 bg-secondary/10"
                            : receivable
                              ? "cursor-pointer border-border border-b hover:bg-muted/30"
                              : "border-border border-b opacity-70"
                        }
                        onClick={
                          receivable
                            ? () => setSelectedSku(line.shopProductSkuId)
                            : undefined
                        }
                      >
                        <td className="px-3 py-2.5">{line.productName}</td>
                        <td className="px-3 py-2.5 font-mono text-xs">
                          {line.shopSku}
                        </td>
                        <td className="px-3 py-2.5 text-muted-foreground text-xs">
                          {trackingLabel(
                            line.trackingMode === "serial"
                              ? "lot_serial"
                              : line.trackingMode,
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">
                          {line.qtyOrdered} / {line.qtyReceived}
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium tabular-nums">
                          {receivable ? remaining : "—"}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">
                          {line.unitCost != null
                            ? formatShopMoney(line.unitCost, d.currency)
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <RHF.Controller
            name="branchId"
            control={form.control}
            render={({ field }) => <input type="hidden" {...field} />}
          />

          <div className="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-border border-b pb-3">
              <div>
                <h2 className="font-medium text-sm">ບັນທຶກການຮັບ</h2>
                {selectedLine && activeSku ? (
                  <p className="mt-1 text-muted-foreground text-xs">
                    {activeSku.productName} · ຄ້າງຮັບບໍ່ເກີນ{" "}
                    <span className="font-medium text-foreground tabular-nums">
                      {poRemaining}
                    </span>{" "}
                    ຊິ້ນ
                  </p>
                ) : (
                  <p className="mt-1 text-muted-foreground text-xs">
                    ເລືອກບັນທັດຈາກຕາງເທິງ
                  </p>
                )}
                <p className="mt-2 text-[11px] text-muted-foreground leading-snug">
                  ສະລັບແຖວໃນຕາຕາ PO ແລ້ວ ຂໍ້ມູນທີ່ກອກຈະຖືກຈືມໃຫ້ຕໍ່ບັນທັດ —
                  ກັບມາບັນທັດເດີມຈະເຫັນຄ່າຄືເດີມ. ກົດບັນທຶກຄັ້ງດຽວຈະຮັບທຸກບັນທັດທີ່ກອກຄົບ
                  (ບັນທັດທີ່ຍັງວ່າງ ຫຼື ບໍ່ມີ lot/serial ຈະຖືກຂ້າມ)
                </p>
              </div>
            </div>

            {selectedSkuId && skuQuery.isLoading ? (
              <div className="flex justify-center py-10">
                <Loader />
              </div>
            ) : null}

            {activeSku && selectedLine ? (
              <>
                <InventorySkuStockBanner
                  productName={activeSku.productName}
                  shopSku={activeSku.shopSku}
                  metaLines={[
                    `ໂໝດ ${trackingLabel(mode)}`,
                    `ຕາມ PO: ສັ່ງ ${selectedLine.qtyOrdered} · ຮັບແລ້ວ ${selectedLine.qtyReceived}`,
                  ]}
                  stockArea={
                    !branchIdForStock ? (
                      <p className="text-muted-foreground text-sm sm:text-right">
                        ບໍ່ມີສາຂາ
                      </p>
                    ) : branchStock.isLoading ? (
                      <InventoryStockLoading />
                    ) : (
                      <>
                        <InventoryStockHero
                          value={
                            stockLine?.quantityOnHand != null
                              ? String(stockLine.quantityOnHand)
                              : "—"
                          }
                        />
                        {stockLine != null &&
                        stockLine.quantityReserved != null &&
                        stockLine.quantityReserved > 0 ? (
                          <p className="mt-1 text-muted-foreground text-xs sm:text-right">
                            ຈອງ:{" "}
                            <span className="tabular-nums">
                              {stockLine.quantityReserved}
                            </span>
                          </p>
                        ) : null}
                      </>
                    )
                  }
                  footnote={
                    activeSku.quantityOnHand != null ? (
                      <>
                        ລວມທົ່ວຮ້ານ:{" "}
                        <span className="font-medium text-foreground tabular-nums">
                          {activeSku.quantityOnHand}
                        </span>
                      </>
                    ) : (
                      "ລວມທົ່ວຮ້ານ — ຍັງບໍ່ຕິດຕາມສະຕ໋ອກລວມ"
                    )
                  }
                />

                <FormRoot<ReceiveFormValues>
                  methods={form}
                  onSubmit={(v) => void onSubmit(v)}
                  className="mt-2 space-y-4"
                >
                  {mode === "none" ? (
                    <FormNumber
                      name="qty"
                      label="ຈຳນວນຮັບເຂົ້າ (ລັອກຕາມຄ້າງຮັບ PO)"
                      requiredMark
                      placeholder="ຕົວເລກເຕັມບວກ"
                      min={1}
                      max={poRemaining > 0 ? poRemaining : 1}
                    />
                  ) : null}

                  {mode === "lot" ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label>ລາຍການ lot</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 gap-1 text-xs"
                          disabled={lotQtyAtPoLimit}
                          onClick={() =>
                            append({
                              lotNo: "",
                              qty: 1,
                              manufacturedDate: "",
                              expiryDate: "",
                            })
                          }
                        >
                          <PlusIcon className="size-3.5" />
                          ເພີ່ມ lot
                        </Button>
                      </div>
                      <div className="max-h-[28rem] space-y-3 overflow-y-auto rounded-md border border-border p-3">
                        {fields.map((f, i) => {
                          const rowQtyRaw = Number(lotsWatched[i]?.qty);
                          const safeRowQty = Number.isFinite(rowQtyRaw)
                            ? rowQtyRaw
                            : 0;
                          const otherSum = lotQtySum - safeRowQty;
                          const headroom = poRemaining - otherSum;
                          const maxQtyForRow = Math.max(0, headroom);
                          const minQtyForRow = maxQtyForRow === 0 ? 0 : 1;
                          return (
                            <div
                              key={f.id}
                              className="grid gap-3 border-border border-b pb-3 last:border-0 sm:grid-cols-12"
                            >
                              <div className="sm:col-span-3">
                                <Label className="text-xs">Lot no.</Label>
                                <Input
                                  className="mt-1 h-9 font-mono text-sm"
                                  {...form.register(`lots.${i}.lotNo`)}
                                  placeholder="LOT-001"
                                />
                              </div>
                              <div className="sm:col-span-2">
                                <Label className="text-xs">
                                  ຈຳນວນ (ບໍ່ເກີນຄ້າງຮັບ PO)
                                </Label>
                                <Input
                                  type="number"
                                  min={minQtyForRow}
                                  max={maxQtyForRow === 0 ? 0 : maxQtyForRow}
                                  className="mt-1 h-9 text-sm"
                                  {...form.register(`lots.${i}.qty`, {
                                    valueAsNumber: true,
                                  })}
                                />
                              </div>
                              <div className="sm:col-span-3">
                                <Label className="text-xs">ວັນທີຜະລິດ</Label>
                                <Input
                                  type="date"
                                  className="mt-1 h-9 text-sm"
                                  {...form.register(
                                    `lots.${i}.manufacturedDate`,
                                  )}
                                />
                              </div>
                              <div className="sm:col-span-3">
                                <Label className="text-xs">ໝົດອາຍຸ</Label>
                                <Input
                                  type="date"
                                  className="mt-1 h-9 text-sm"
                                  {...form.register(`lots.${i}.expiryDate`)}
                                />
                              </div>
                              <div className="flex items-end sm:col-span-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-9 text-destructive"
                                  disabled={fields.length <= 1}
                                  onClick={() => remove(i)}
                                  aria-label="ລົບ lot"
                                >
                                  <Trash2Icon className="size-4" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="space-y-2 rounded-md border border-border/70 bg-muted/20 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <Label className="text-sm">
                            Lot scan labels (Code 128)
                          </Label>
                          {lotLabelsQuery.isFetching ? <Loader /> : null}
                        </div>
                        {!branchIdForStock || !selectedSkuId ? (
                          <p className="text-muted-foreground text-xs">
                            ຂໍ້ມູນສາຂາ/SKU ບໍ່ຄົບ
                          </p>
                        ) : lotLabelsQuery.isLoading ? (
                          <div className="flex justify-center py-3">
                            <Loader />
                          </div>
                        ) : lotLabelsQuery.isError ? (
                          <p className="text-destructive text-xs">
                            {lotLabelsQuery.error instanceof Error
                              ? lotLabelsQuery.error.message
                              : "ໂຫຼດ lot labels ບໍ່ສຳເລັດ"}
                          </p>
                        ) : !lotLabelsQuery.data?.items?.length ? (
                          <p className="text-muted-foreground text-xs">
                            ບໍ່ພົບ lot ສຳລັບ SKU ນີ້ໃນສາຂານີ້
                          </p>
                        ) : (
                          <div className="max-h-64 space-y-2 overflow-y-auto">
                            {lotLabelsQuery.data.items.map((lot) => {
                              const draft = lotScanDraftById[lot.id];
                              const live = draft ?? lot.lotScanCode ?? "";
                              return (
                                <div
                                  key={lot.id}
                                  className="grid gap-2 rounded-md border border-border/70 bg-card p-2 sm:grid-cols-12 sm:items-end"
                                >
                                  <div className="sm:col-span-2">
                                    <Label className="text-xs">Lot</Label>
                                    <p className="mt-1 font-mono text-xs">
                                      {lot.lotNo}
                                    </p>
                                  </div>
                                  <div className="sm:col-span-4">
                                    <Label className="text-xs">Scan code</Label>
                                    <Input
                                      className="mt-1 h-9 font-mono text-xs"
                                      value={live}
                                      onChange={(e) =>
                                        setLotScanDraftById((p) => ({
                                          ...p,
                                          [lot.id]: e.target.value,
                                        }))
                                      }
                                      placeholder="LXXXXXXXXXXXXXX"
                                    />
                                  </div>
                                  <div className="flex flex-wrap gap-1 sm:col-span-6 sm:justify-end">
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="h-8 text-xs"
                                      onClick={() =>
                                        void saveLotScanCode(lot.id)
                                      }
                                      disabled={patchLotScan.isPending}
                                    >
                                      ບັນທຶກ
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="h-8 text-xs"
                                      onClick={() =>
                                        void regenerateLotScanCode(lot.id)
                                      }
                                      disabled={patchLotScan.isPending}
                                    >
                                      ສ້າງໃໝ່
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      className="h-8 text-xs"
                                      onClick={() =>
                                        printLotLabelCode128({
                                          lotScanCode: live,
                                          lotNo: lot.lotNo,
                                          productName: activeSku.productName,
                                          shopSku: activeSku.shopSku,
                                          expiryDate: lot.expiryDate,
                                        })
                                      }
                                      disabled={!live}
                                    >
                                      ພິມປ້າຍ
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}

                  {mode === "lot_serial" ? (
                    <div className="space-y-4">
                      <p className="text-muted-foreground text-xs leading-snug">
                        ແຕ່ລະກຸ່ມ = 1 lot — ໃສ່ Lot no. ກ່ອນ ຈາກນັ້ນເພີ່ມ serial
                        ຕໍ່ຊິ້ນໃນກຸ່ມນັ້ນ. ລະຫັດສະແກນຫນ່ວຍສ້າງອັດຕະໂນມັດເປັນ{" "}
                        <span className="font-mono text-foreground">
                          Lot|Serial
                        </span>
                      </p>
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-sm">ກຸ່ມຕາມ Lot</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 gap-1 text-xs"
                          disabled={serialRowsAtPoLimit}
                          onClick={() =>
                            appendSerialGroup({
                              lotNo: "",
                              items: [{ serialNo: "", unitScanCode: "" }],
                            })
                          }
                        >
                          <PlusIcon className="size-3.5" />
                          ເພີ່ມກຸ່ມ lot
                        </Button>
                      </div>
                      <div className="space-y-4 overflow-y-auto pr-1">
                        {serialGroupFields.map((g, gi) => (
                          <SerialLotGroupBlock
                            key={g.id}
                            groupIndex={gi}
                            form={form}
                            canRemoveGroup={serialGroupFields.length > 1}
                            onRemoveGroup={() => removeSerialGroup(gi)}
                            disableAddSerial={serialRowsAtPoLimit}
                          />
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {warehouseLocationsOn && branchIdForStock ? (
                    <div className="space-y-2 rounded-md border border-border/70 bg-muted/20 p-3">
                      <p className="font-medium text-sm">
                        ຊ່ອງຮັບເຂົ້າ (ບັນທັດນີ້)
                      </p>
                      <RHF.Controller
                        name="locationId"
                        control={form.control}
                        render={({ field }) => (
                          <ShopWarehouseLocationPickerRemote
                            shopId={shopId}
                            branchId={branchIdForStock}
                            value={field.value ?? ""}
                            onValueChange={field.onChange}
                            placeholder="ໃຊ້ default ທັງ PO (ວ່າງ = ຕາມຂ້າງເທິງ)"
                            className="w-full max-w-md"
                          />
                        )}
                      />
                      <p className="text-muted-foreground text-xs">
                        ວ່າງໄວ້ = ໃຊ້ຊ່ອງ default ທັງ PO ຫຼື ຊ່ອງ default ສາຂາ
                      </p>
                    </div>
                  ) : null}

                  <FormTextarea
                    name="note"
                    label="ໝາຍເຫດ (ທາງເລືອກ)"
                    placeholder="ອ້າງອີງໃບສົ່ງ..."
                    rows={2}
                  />

                  <div className="flex flex-wrap justify-end gap-2 pt-2">
                    <Button
                      variant="outline"
                      type="button"
                      onClick={() =>
                        nav({
                          to: PO_DETAIL_ROUTE,
                          params: { shopId, purchaseOrderId },
                        })
                      }
                    >
                      ຍົກເລີກ
                    </Button>
                    <Button type="submit" disabled={receivePo.isPending}>
                      {receivePo.isPending ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader />
                          ກຳລັງບັນທຶກ
                        </span>
                      ) : (
                        "ບັນທຶກຮັບທຸກບັນທັດທີ່ກອກ"
                      )}
                    </Button>
                  </div>
                </FormRoot>
              </>
            ) : selectedSkuId && !skuQuery.isLoading && skuQuery.isError ? (
              <p className="text-destructive text-sm">ໂຫຼດ SKU ບໍ່ສຳເລັດ</p>
            ) : null}
          </div>
        </div>
      </Main>
    </RequireMerchantPermission>
  );
}
